import { useState, useEffect } from 'react';
import { Button } from '../../../components/base/Button';

const ReservationSection = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    retreatDate: '',
    retreatDuration: '',
    accommodationType: '',
    dietaryRequirements: '',
    healthConditions: '',
    previousExperience: '',
    specialRequests: '',
    emergencyContact: '',
    emergencyPhone: '',
    agreeTerms: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [totalPrice, setTotalPrice] = useState(0);
  const [depositAmount, setDepositAmount] = useState(0);

  useEffect(() => {
    let total = 0;
    if (formData.accommodationType === 'shared-room' || formData.accommodationType === 'no-preference') {
      total = 1500;
    } else if (formData.accommodationType === 'private-room') {
      total = 1800;
    }
    setTotalPrice(total);
    setDepositAmount(Math.round(total * 0.5));
  }, [formData.accommodationType]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // First, submit booking details to form
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, String(value));
      });

      const formResponse = await fetch('https://readdy.ai/api/form/d4tbmdkapcs36ntc0ccg', {
        method: 'POST',
        body: formDataToSend
      });

      if (!formResponse.ok) {
        throw new Error('Form submission failed');
      }

      // Then create Stripe checkout session
      const checkoutResponse = await fetch('https://cemdjgdhorlezbdrpwej.supabase.co/functions/v1/create-retreat-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          retreatDuration: formData.retreatDuration,
          accommodationType: formData.accommodationType,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          retreatDate: formData.retreatDate,
        }),
      });

      if (!checkoutResponse.ok) {
        throw new Error('Payment session creation failed');
      }

      const { url } = await checkoutResponse.json();
      
      // Redirect to Stripe checkout
      window.location.href = url;
      
    } catch (error) {
      setSubmitMessage('Sorry, there was an error processing your booking. Please try again or contact us directly at +34 643 939 427.');
      setIsSubmitting(false);
    }
  };

  // Check for payment status in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'success') {
      setSubmitMessage('Thank you for your deposit payment! Your reservation is confirmed. We will contact you within 24 hours with all the details and information about the remaining balance.');
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      setSubmitMessage('Payment was cancelled. If you need assistance, please contact us at +34 643 939 427.');
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <section id="reserve" className="py-24 bg-gradient-to-br from-white to-sand-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div 
            className="w-full h-64 bg-cover bg-center rounded-3xl mb-12 shadow-xl"
            style={{
              backgroundImage: `url('https://public.readdy.ai/ai/img_res/44e6a5389085a65e593321d39fb44bfd.jpg')`
            }}
          />
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
            Reserve Your Spot
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            <strong>Dates: March, April, May, June</strong><br />
            Limited spots available – secure your place now to embark on a journey of Ayurvedic healing, mindful living, and soulful transformation.
          </p>
          
          {/* Contact Number */}
          <div className="bg-primary-50 border-2 border-primary-200 rounded-2xl p-6 max-w-md mx-auto">
            <p className="text-sm text-gray-700 mb-3 font-medium">Questions? Call us directly</p>
            <a 
              href="tel:+34643939427" 
              className="inline-flex items-center text-primary-600 hover:text-primary-700 text-2xl font-bold transition-colors cursor-pointer"
            >
              <i className="ri-phone-fill mr-3 text-3xl"></i>
              +34 643 939 427
            </a>
          </div>
        </div>

        {/* Reservation Form */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          {/* Pricing Summary */}
          {totalPrice > 0 && (
            <div className="bg-gradient-to-r from-primary-50 to-sage-50 rounded-2xl p-6 mb-8 border border-primary-100">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">Retreat Package</h3>
                  <p className="text-sm text-gray-600">
                    {formData.accommodationType === 'private-room' ? 'Private room — all-inclusive experience' : 'Shared room — all-inclusive experience'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-primary-600">
                    €{totalPrice.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex justify-between items-center pt-4 mt-2 border-t-2 border-primary-300">
                <p className="text-lg font-bold text-gray-900">Total Amount</p>
                <p className="text-3xl font-bold text-primary-600">€{totalPrice.toLocaleString()}</p>
              </div>
              <div className="flex justify-between items-center pt-4 mt-4 border-t border-primary-200 bg-green-50 -mx-6 px-6 py-4 rounded-b-2xl">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pay Today (50% Deposit)</p>
                  <p className="text-xs text-gray-500">Remaining balance due before retreat</p>
                </div>
                <p className="text-2xl font-bold text-green-600">€{depositAmount.toLocaleString()}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} data-readdy-form id="retreat-reservation-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Personal Information */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                />
              </div>

              <div>
                <label htmlFor="retreatDuration" className="block text-sm font-semibold text-gray-700 mb-2">
                  Retreat Duration *
                </label>
                <select
                  id="retreatDuration"
                  name="retreatDuration"
                  value={formData.retreatDuration}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                >
                  <option value="">Select duration</option>
                  <option value="4-days">4 Days (3 Nights)</option>
                  <option value="7-nights">7 Nights</option>
                </select>
              </div>

              <div>
                <label htmlFor="retreatDate" className="block text-sm font-semibold text-gray-700 mb-2">
                  Preferred Retreat Date *
                </label>
                <select
                  id="retreatDate"
                  name="retreatDate"
                  value={formData.retreatDate}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                >
                  <option value="">Select a date</option>
                  <option value="mar-18-21">March 18-21, 2025</option>
                  <option value="apr-2-5">April 2-5, 2025</option>
                  <option value="may-14-17">May 14-17, 2025</option>
                  <option value="jun-11-14">June 11-14, 2025</option>
                </select>
              </div>

              <div>
                <label htmlFor="accommodationType" className="block text-sm font-semibold text-gray-700 mb-2">
                  Accommodation Preference
                </label>
                <select
                  id="accommodationType"
                  name="accommodationType"
                  value={formData.accommodationType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                >
                  <option value="">Select accommodation</option>
                  <option value="shared-room">Shared Room — €1,500</option>
                  <option value="private-room">Private Room — €1,800</option>
                  <option value="no-preference">No Preference — €1,500</option>
                </select>
              </div>
            </div>

            {/* Additional Information */}
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label htmlFor="dietaryRequirements" className="block text-sm font-semibold text-gray-700 mb-2">
                  Dietary Requirements or Food Allergies
                </label>
                <textarea
                  id="dietaryRequirements"
                  name="dietaryRequirements"
                  value={formData.dietaryRequirements}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                  placeholder="Please describe any dietary restrictions, allergies, or preferences"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.dietaryRequirements.length}/500 characters</p>
              </div>

              <div>
                <label htmlFor="healthConditions" className="block text-sm font-semibold text-gray-700 mb-2">
                  Health Conditions or Physical Limitations
                </label>
                <textarea
                  id="healthConditions"
                  name="healthConditions"
                  value={formData.healthConditions}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                  placeholder="Please share any health conditions or physical limitations we should be aware of"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.healthConditions.length}/500 characters</p>
              </div>

              <div>
                <label htmlFor="previousExperience" className="block text-sm font-semibold text-gray-700 mb-2">
                  Previous Experience with Yoga/Ayurveda
                </label>
                <select
                  id="previousExperience"
                  name="previousExperience"
                  value={formData.previousExperience}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                >
                  <option value="">Select your experience level</option>
                  <option value="beginner">Complete Beginner</option>
                  <option value="some-experience">Some Experience (1-2 years)</option>
                  <option value="intermediate">Intermediate (3-5 years)</option>
                  <option value="advanced">Advanced (5+ years)</option>
                </select>
              </div>

              <div>
                <label htmlFor="specialRequests" className="block text-sm font-semibold text-gray-700 mb-2">
                  Special Requests or Questions
                </label>
                <textarea
                  id="specialRequests"
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                  placeholder="Any special requests, questions, or additional information you'd like to share"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.specialRequests.length}/500 characters</p>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="emergencyContact" className="block text-sm font-semibold text-gray-700 mb-2">
                  Emergency Contact Name *
                </label>
                <input
                  type="text"
                  id="emergencyContact"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                />
              </div>

              <div>
                <label htmlFor="emergencyPhone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Emergency Contact Phone *
                </label>
                <input
                  type="tel"
                  id="emergencyPhone"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                />
              </div>
            </div>

            {/* Terms Agreement */}
            <div className="mb-8">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleInputChange}
                  required
                  className="mt-1 mr-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700 leading-relaxed">
                  I agree to the terms and conditions, understand that this retreat involves physical activities, 
                  and confirm that I am in good health to participate. I acknowledge that a 50% deposit is required 
                  to secure my reservation. *
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <Button 
                type="submit" 
                size="xl" 
                disabled={isSubmitting}
                className="bg-primary-500 hover:bg-primary-600 text-white px-12 py-4 text-lg whitespace-nowrap"
              >
                {isSubmitting ? 'Processing...' : 'Reserve My Spot'}
              </Button>
            </div>

            {/* Submit Message */}
            {submitMessage && (
              <div className={`mt-6 p-4 rounded-lg text-center ${
                submitMessage.includes('Thank you') 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {submitMessage}
              </div>
            )}
          </form>

          {/* Additional Info */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <i className="ri-secure-payment-line text-3xl text-primary-500 mb-3"></i>
                <h4 className="font-semibold text-gray-900 mb-2">Secure Payment</h4>
                <p className="text-sm text-gray-600">Pay 50% deposit now, remaining balance before retreat</p>
              </div>
              <div>
                <i className="ri-calendar-check-line text-3xl text-sage-500 mb-3"></i>
                <h4 className="font-semibold text-gray-900 mb-2">Flexible Booking</h4>
                <p className="text-sm text-gray-600">Free cancellation up to 14 days before retreat date</p>
              </div>
              <div>
                <i className="ri-customer-service-2-line text-3xl text-gold-500 mb-3"></i>
                <h4 className="font-semibold text-gray-900 mb-2">Personal Support</h4>
                <p className="text-sm text-gray-600">Dedicated retreat coordinator to assist with all arrangements</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReservationSection;